import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
    Button, Flex, Form, Input, Modal, Select, Spin,
} from 'antd';
import {
    useCallback, useMemo,
} from 'react';
import {
    Controller, useForm,
} from 'react-hook-form';
import { z } from 'zod';

import { supportTicketsMutations } from '@/api';
import {
    SupportTicketPriority, SupportTicketType,
} from '@/constants';
import { SupportTicketCreatePayload } from '@/types';

import { PriorityTag } from './PriorityTag';
import { TypeTag } from './TypeTag';

const supportTicketTypeValues = Object.values(SupportTicketType) as [SupportTicketType, ...SupportTicketType[]];
const supportTicketPriorityValues = Object.values(SupportTicketPriority) as [SupportTicketPriority, ...SupportTicketPriority[]];

const supportTicketSchema = z.object({
    subject: z.string().min(5, 'Мінімум 5 символів').max(255, 'Максимум 255 символів').trim(),
    description: z.string().min(20, 'Мінімум 20 символів'),
    type: z.enum(supportTicketTypeValues),
    priority: z.enum(supportTicketPriorityValues).optional().nullable(),
});

type SupportTicketFormData = z.infer<typeof supportTicketSchema>;

type SupportTicketModalProps = {
    open: boolean;
    onClose: () => void;
};

const typeOptions = Object.values(SupportTicketType).map((value) => ({
    value,
    label: <TypeTag type={value} />,
}));

const priorityOptions = Object.values(SupportTicketPriority).map((value) => ({
    value,
    label: <PriorityTag priority={value} />,
}));

export const SupportTicketModal = ({ open, onClose }: SupportTicketModalProps) => {
    const defaultValues = useMemo<SupportTicketFormData>(() => ({
        subject: '',
        description: '',
        type: SupportTicketType.Question,
        priority: null,
    }), []);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<SupportTicketFormData>({
        resolver: zodResolver(supportTicketSchema),
        defaultValues,
        mode: 'onChange',
    });

    const createMutationOptions = supportTicketsMutations.create();
    const { mutate: createSupportTicket, isPending } = useMutation(createMutationOptions);

    const handleFormSubmit = useCallback((data: SupportTicketFormData) => {
        const payload: SupportTicketCreatePayload = {
            subject: data.subject,
            description: data.description,
            type: data.type,
            priority: data.priority || undefined,
        };

        createSupportTicket(payload, {
            onSuccess: () => {
                reset();
                onClose();
            },
        });
    }, [createSupportTicket, reset, onClose]);

    const handleCancel = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    return (
        <Modal
            title="Запит на підтримку"
            open={open}
            onCancel={handleCancel}
            footer={null}
            width={800}
        >
            <Spin spinning={isPending}>
                <Form
                    layout="vertical"
                    onFinish={handleSubmit(handleFormSubmit)}
                >
                    <Controller
                        name="subject"
                        control={control}
                        render={({ field }) => (
                            <Form.Item
                                label="Тема"
                                required
                                validateStatus={errors.subject ? 'error' : ''}
                                help={errors.subject?.message}
                            >
                                <Input
                                    {...field}
                                    placeholder="Введіть тему запиту"
                                    maxLength={255}
                                    showCount
                                    disabled={isPending}
                                />
                            </Form.Item>
                        )}
                    />

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <Form.Item
                                label="Опис"
                                required
                                validateStatus={errors.description ? 'error' : ''}
                                help={errors.description?.message}
                            >
                                <Input.TextArea
                                    {...field}
                                    placeholder="Введіть опис запиту"
                                    rows={4}
                                    disabled={isPending}
                                />
                            </Form.Item>
                        )}
                    />

                    <Flex gap="middle">
                        <Controller
                            name="type"
                            control={control}
                            render={({ field }) => (
                                <Form.Item
                                    label="Тип"
                                    required
                                    validateStatus={errors.type ? 'error' : ''}
                                    help={errors.type?.message}
                                    style={{ flex: 1 }}
                                >
                                    <Select
                                        {...field}
                                        placeholder="Виберіть тип"
                                        options={typeOptions}
                                        disabled={isPending}
                                        tagRender={(props) => {
                                            const { value } = props;
                                            if (!value) {
                                                return <span />;
                                            }
                                            return (
                                                <TypeTag type={value as SupportTicketType} />
                                            );
                                        }}
                                    />
                                </Form.Item>
                            )}
                        />

                        <Controller
                            name="priority"
                            control={control}
                            render={({ field }) => (
                                <Form.Item
                                    label="Пріоритет"
                                    validateStatus={errors.priority ? 'error' : ''}
                                    help={errors.priority?.message}
                                    style={{ flex: 1 }}
                                >
                                    <Select
                                        {...field}
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Виберіть пріоритет"
                                        options={priorityOptions}
                                        allowClear
                                        disabled={isPending}
                                        tagRender={(props) => {
                                            const { value } = props;
                                            if (!value) {
                                                return <span />;
                                            }
                                            return (
                                                <PriorityTag priority={value as SupportTicketPriority} />
                                            );
                                        }}
                                    />
                                </Form.Item>
                            )}
                        />
                    </Flex>

                    <Form.Item>
                        <Flex gap="middle" justify="end">
                            <Button
                                onClick={handleCancel}
                                disabled={isPending}
                            >
                                Скасувати
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={isPending}
                                disabled={isPending}
                            >
                                Відправити
                            </Button>
                        </Flex>
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};
